/**
 * Difficulté jouée : deux vues, une même donnée source.
 *
 * `variant="volume"`  → combien d'apprentissages par niveau
 * `variant="errors"`  → taux d'erreur moyen par niveau
 *
 * Deux cartes distinctes et non un graphique à double axe : un volume en
 * dizaines de milliers et un taux en pourcentage n'ont aucune échelle commune,
 * et les superposer ferait croire à des croisements qui n'existent pas.
 *
 * Colonnes verticales ici (et non barres horizontales) : les libellés sont des
 * chiffres, l'ordre 1→5 est naturel de gauche à droite.
 */

import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatCompact, formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { DataColumn, DifficultyItem } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

const columns: ReadonlyArray<DataColumn<DifficultyItem>> = [
  { key: 'difficulty', header: 'Niveau', numeric: true, render: (row) => String(row.difficulty) },
  { key: 'learns', header: 'Apprentissages', numeric: true, render: (row) => formatInteger(row.learns) },
  {
    key: 'error',
    header: "Taux d'erreur",
    numeric: true,
    render: (row) => formatPercent(row.errorRatePercent),
  },
];

interface Props {
  data: readonly DifficultyItem[];
  variant: 'volume' | 'errors';
}

export function DifficultyChart({ data, variant }: Props) {
  const isVolume = variant === 'volume';
  const dataKey = isVolume ? 'learns' : 'errorRatePercent';
  const format = isVolume
    ? (value: number) => formatInteger(value)
    : (value: number) => formatPercent(value);

  return (
    <ChartCard
      title={isVolume ? 'Niveaux de difficulté joués' : "Taux d'erreur par niveau"}
      subtitle={
        isVolume
          ? 'Difficulté annoncée par le catalogue, de 1 à 5'
          : 'Fausses notes rapportées aux notes jouées, par niveau'
      }
      rows={data}
      columns={columns}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data as DifficultyItem[]} margin={{ top: 20, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="difficulty"
            tickFormatter={(value: number) => `Niv. ${value}`}
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={isVolume ? formatCompact : (value: number) => `${value}\u202F%`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={format} />}
          />
          <Bar
            isAnimationActive={false}
            dataKey={dataKey}
            name={isVolume ? 'Apprentissages' : "Taux d'erreur"}
            fill={COLORS.series1}
            // Arrondi sur la tete de colonne, carre sur la ligne de base.
            radius={[4, 4, 0, 0]}
            maxBarSize={BAR_MAX_SIZE}
          >
            <LabelList
              dataKey={dataKey}
              position="top"
              fill={COLORS.textSecondary}
              fontSize={12}
              formatter={(value: unknown) => format(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
